import React, { useCallback, useEffect } from "react";
import { useBlocker, type Location } from "react-router-dom";
import { intent, timeout, icon } from "modules/root/components/app/Notifications";
import { Notificator } from "modules/root/components/app/toaster";

/**
 * Запрет ухода со страницы с несохранёнными данными: переход отменяется молча,
 * а пользователю показывается всплывающее сообщение.
 *
 * В react-router 5 это делалось через `history.block`, которого в шестой версии
 * нет — вместо него `useBlocker` у data-роутера. Отмена перехода там называется
 * `blocker.reset()`, поведение то же самое.
 *
 * Хук поднимается только когда блокировка включена: роутер держит один активный
 * блокировщик, а на карточке заказа таких компонентов смонтировано сразу
 * несколько (вкладки рисуются все разом). Пока «грязная» вкладка одна — активен
 * ровно один блокировщик, как и было с `history.block`.
 */
const DenyNavigation = ({ when, message, skip }: DenyNavigationProps) => {
    useEffect(() => {
        if (!when) {
            return undefined;
        }

        // Закрытие вкладки и перезагрузку держит браузер — это отдельная от роутера
        // история. Слушатель свой у каждого экземпляра, а не общий слот
        // `window.onbeforeunload`: на карточке заказа таких компонентов смонтировано
        // сразу шесть, и при размонтировании соседа общий слот обнулялся, после чего
        // «грязная» вкладка молча перестала бы предупреждать о потере данных.
        window.addEventListener("beforeunload", stop);

        return () => {
            window.removeEventListener("beforeunload", stop);
        };
    }, [when]);

    return when ? <Blocker message={message} skip={skip} /> : null;
};

const Blocker = ({ message, skip }: Omit<DenyNavigationProps, "when">) => {
    const shouldBlock = useCallback(
        ({ nextLocation }: { nextLocation: Location }) => !(skip ? skip(nextLocation) : false),
        [skip]
    );

    const blocker = useBlocker(shouldBlock);

    useEffect(() => {
        if (blocker.state !== "blocked") {
            return;
        }

        const type = "error";

        Notificator.show({
            message,
            icon: icon(type),
            intent: intent(type),
            timeout: timeout(type),
        });

        blocker.reset();
    }, [blocker, message]);

    return null;
};

export default DenyNavigation;

interface DenyNavigationProps {
    when: boolean;
    message: string;
    skip?: (tx: Location) => boolean;
}

const stop = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    // старые браузеры показывают вопрос только при непустом returnValue
    e.returnValue = "";
};
